'use client';

import type { ReactNode } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { Drawer } from 'vaul';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@v2/ui';
import { cn } from '@v2/utils';

const DESKTOP_QUERY = '(min-width: 1024px)';

export function useIsDesktopHotelFilters() {
  return useMediaQuery(DESKTOP_QUERY, { initializeWithValue: false });
}

const panel = 'w-[min(420px,calc(100vw-32px))] rounded-2xl p-6 shadow-xl';

const hotelDrawerSheetClassName =
  'fixed inset-x-0 bottom-0 z-[70] mx-auto flex h-auto max-h-[min(90svh,48rem)] w-full max-w-[520px] flex-col overflow-hidden rounded-t-[24px] bg-white px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-shadowLg outline-none sm:px-6';

function preventDismissForNestedOverlay(event: {
  preventDefault: () => void;
  target: EventTarget;
}) {
  const target = event.target as Element | null;
  if (
    target?.closest?.('[data-radix-popper-content-wrapper]') ||
    target?.closest?.('[data-radix-dropdown-menu-content]') ||
    target?.closest?.('[role="listbox"]')
  ) {
    event.preventDefault();
  }
}

export function HotelFilterActions({
  onReset,
  onApply,
  applyLabel = 'Apply',
  resetLabel = 'Reset',
  resetDisabled,
  layout,
}: {
  onReset: () => void;
  onApply: () => void;
  applyLabel?: string;
  resetLabel?: string;
  resetDisabled?: boolean;
  layout: 'popover' | 'sheet';
}) {
  if (layout === 'sheet') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Button
          type="button"
          variant="secondary"
          className="min-h-12 w-full rounded-xl text-base font-semibold"
          disabled={resetDisabled}
          onClick={onReset}
        >
          {resetLabel}
        </Button>
        <Button
          type="button"
          className="min-h-12 w-full rounded-xl text-base font-semibold"
          onClick={onApply}
        >
          {applyLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-6 flex items-center justify-between">
      <Button
        type="button"
        variant="link"
        disabled={resetDisabled}
        onClick={onReset}
      >
        {resetLabel}
      </Button>
      <Button type="button" onClick={onApply}>
        {applyLabel}
      </Button>
    </div>
  );
}

export function HotelFilterSheet({
  open,
  onOpenChange,
  title,
  description,
  trigger,
  children,
  footer,
  contentClassName,
  ariaLabel,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: string;
  trigger: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  contentClassName?: string;
  ariaLabel?: string;
}) {
  const isDesktop = useIsDesktopHotelFilters();
  const labelledBy = ariaLabel ?? title;

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          aria-label={labelledBy}
          align="start"
          className={cn(panel, contentClassName)}
        >
          {children}
          {footer}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer.Root
      handleOnly
      repositionInputs={false}
      shouldScaleBackground={false}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Drawer.Trigger asChild>{trigger}</Drawer.Trigger>
      {open === false ? null : (
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-[70] bg-gray-950/45" />
          <Drawer.Content
            aria-label={labelledBy}
            className={hotelDrawerSheetClassName}
            style={{ pointerEvents: 'auto' }}
            onPointerDownOutside={preventDismissForNestedOverlay}
            onFocusOutside={preventDismissForNestedOverlay}
            onInteractOutside={preventDismissForNestedOverlay}
          >
            <Drawer.Handle className="mx-auto mb-2 mt-3 h-1.5 w-10 shrink-0 rounded-full bg-gray-300" />
            <div className="flex shrink-0 flex-col px-2 pb-3 pt-2 text-center">
              <Drawer.Title className="text-xl font-extrabold leading-7 tracking-[-0.4px] text-gray-950">
                {title}
              </Drawer.Title>
              <Drawer.Description
                className={
                  description
                    ? 'mt-1 text-sm font-medium text-gray-700'
                    : 'sr-only'
                }
              >
                {description ?? title}
              </Drawer.Description>
            </div>
            <div
              data-vaul-no-drag
              className="min-h-0 grow overflow-y-auto overscroll-contain px-2 pb-4"
            >
              {children}
            </div>
            {footer ? (
              <div className="mt-auto shrink-0 border-t border-gray-200 bg-white px-2 pb-0 pt-4">
                {footer}
              </div>
            ) : null}
          </Drawer.Content>
        </Drawer.Portal>
      )}
    </Drawer.Root>
  );
}
